import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Lock, Radio } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BATCH_STATUS_META, fmtIDR, fmtLocal } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";

const BUFFER_TYPE_LABELS = {
  PURCHASE_SURPLUS: "Purchase surplus",
  ALLOCATED_TO_NEW_ORDER: "Allocated to new demand",
  AUTO_ORDER_ALLOCATION: "Auto-allocated to customer order",
  AUTO_ORDER_ALLOCATION_REVERSAL: "Buffer restored (order cancelled)",
  OWNER_ADJUSTMENT: "Owner adjustment",
};

function Stat({ label, value, testId, valueClass = "text-stone-800" }) {
  return (
    <div className="rounded-md border bg-card p-3 text-center">
      <p data-testid={testId} className={`font-heading text-xl font-extrabold ${valueClass}`}>
        {value}
      </p>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3
      className="rounded-md px-3 py-2 font-heading text-sm font-bold uppercase tracking-wide"
      style={{ backgroundColor: "#D8C9BE", color: "#4E4542" }}
    >
      {children}
    </h3>
  );
}

export default function BatchSummaryView({ batchId, refreshKey }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .get(`/batches/${batchId}/summary`)
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, [batchId, refreshKey]);

  if (error) {
    return (
      <p data-testid="summary-error" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
        {error}
      </p>
    );
  }
  if (!data) {
    return (
      <div className="grid place-items-center py-16" data-testid="summary-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const { batch, purchasing, receiving, buffer, ready_stock, locations = [], products = [], lifecycle = [], financial } = data;
  const closed = batch.status === "closed";
  const showCosts = user.role !== "viewer";
  const opMeta = BATCH_STATUS_META[batch.operational_status] || BATCH_STATUS_META.open;

  return (
    <div className="space-y-5" data-testid="batch-summary-view">
      <div>
        <h2 className="font-heading text-xl font-extrabold tracking-tight" data-testid="summary-batch-name">
          {batch.name}
        </h2>
        <p className="text-sm text-muted-foreground">
          {batch.region || "—"}
          {batch.currency_code && ` · ${batch.currency_code} ${batch.currency_symbol}`}
        </p>
      </div>

      <div
        data-testid="summary-state-banner"
        className={`rounded-md border p-4 ${closed ? "border-stone-400 bg-stone-100" : "border-amber-200 bg-amber-50"}`}
      >
        <div className="flex items-center gap-2">
          {closed ? <Lock className="h-4 w-4 text-stone-600" /> : <Radio className="h-4 w-4 text-amber-600" />}
          <p className={`text-sm font-extrabold ${closed ? "text-stone-700" : "text-amber-700"}`}>
            {closed ? "FINALIZED — Batch Closed" : "LIVE SUMMARY — Not Finalized"}
          </p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {closed
            ? `Closed by ${batch.closed_by || "—"}${batch.closed_at ? ` · ${format(new Date(batch.closed_at), "d MMM yyyy, HH:mm")}` : ""}. Historical purchasing and receiving records are preserved.`
            : "Figures update in real time as purchases and receiving approvals happen. The Owner closes the batch to finalize this summary."}
        </p>
        <span data-testid="summary-op-status" className={`mt-2 inline-flex rounded-md border px-2 py-0.5 text-xs font-bold ${opMeta.badge}`}>
          {opMeta.label}
        </span>
      </div>

      <section data-testid="summary-purchasing" className="space-y-2">
        <SectionTitle>Purchasing Summary</SectionTitle>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Products" value={purchasing.total_products} testId="pur-products" />
          <Stat label="Requested" value={purchasing.requested_qty} testId="pur-requested" />
          <Stat label="Purchased" value={purchasing.purchased_qty} testId="pur-purchased" />
          <Stat
            label="Not Purchased"
            value={`${purchasing.not_purchased_products} products · ${purchasing.not_purchased_qty} units`}
            testId="pur-not-purchased"
            valueClass={purchasing.not_purchased_products > 0 ? "text-stone-500" : "text-stone-800"}
          />
          <Stat
            label="Partial"
            value={`${purchasing.partial_products} products partial · ${purchasing.partial_qty} units left to buy`}
            testId="pur-partial"
            valueClass={purchasing.partial_products > 0 ? "text-amber-600" : "text-stone-800"}
          />
          <Stat label="Buffer Available" value={purchasing.buffer_available} testId="pur-buffer" valueClass="text-emerald-600" />
        </div>
      </section>

      {financial && (
        <section data-testid="summary-financial" className="space-y-2">
          <SectionTitle>Financial Summary (Owner Only)</SectionTitle>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Stat
              label={`Total Cost ${financial.currency_code || ""}`}
              value={fmtLocal(financial.total_actual_cost, financial.currency_symbol)}
              testId="fin-total-cost"
            />
            <Stat label="Converted Cost" value={fmtIDR(financial.total_converted_cost_idr)} testId="fin-converted" />
            <Stat label="Selling Value" value={fmtIDR(financial.total_selling_value_idr)} testId="fin-selling" />
            <Stat
              label="Est. Gross Profit"
              value={fmtIDR(financial.estimated_gross_profit_idr)}
              testId="fin-profit"
              valueClass={financial.estimated_gross_profit_idr < 0 ? "text-red-600" : "text-emerald-600"}
            />
            <Stat
              label="Est. Margin"
              value={financial.estimated_margin_pct != null ? `${financial.estimated_margin_pct}%` : "—"}
              testId="fin-margin"
            />
            <Stat
              label="Margin Warnings"
              value={financial.low_margin_count + financial.very_low_margin_count + financial.negative_margin_count}
              testId="fin-warnings"
              valueClass={financial.negative_margin_count > 0 ? "text-red-600" : "text-stone-800"}
            />
          </div>
        </section>
      )}

      <section data-testid="summary-locations" className="space-y-2">
        <SectionTitle>Location Summary</SectionTitle>
        {locations.length === 0 ? (
          <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">No purchases recorded yet</p>
        ) : (
          <div className="space-y-1.5">
            {locations.map((l) => (
              <div
                key={l.location}
                data-testid={`summary-location-${l.location.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2.5"
              >
                <span className="min-w-0 truncate text-sm font-semibold">{l.location}</span>
                <span className="shrink-0 text-xs font-semibold text-muted-foreground">
                  {l.units} units
                  {showCosts && l.cost_idr != null && ` · ${fmtIDR(l.cost_idr)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="summary-receiving" className="space-y-2">
        <SectionTitle>Receiving Summary</SectionTitle>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Purchased" value={receiving.total_purchased} testId="recv-purchased" />
          <Stat label="Approved Received" value={receiving.total_approved_received} testId="recv-received" />
          <Stat
            label="Remaining to Receive"
            value={receiving.remaining_to_receive}
            testId="recv-remaining"
            valueClass={receiving.remaining_to_receive > 0 ? "text-amber-600" : "text-emerald-600"}
          />
          <Stat label="Sessions" value={receiving.session_count} testId="recv-sessions" />
          <Stat label="Open Sessions" value={receiving.open_sessions} testId="recv-open-sessions" />
        </div>
        {receiving.last_approval && (
          <p data-testid="recv-last-approval" className="text-xs text-muted-foreground">
            Last approval by {receiving.last_approval.by_name}
            {receiving.last_approval.by_role === "owner" ? " (Owner Override)" : receiving.last_approval.by_role ? " (Admin Checker)" : ""}
            {receiving.last_approval.at && ` · ${format(new Date(receiving.last_approval.at), "d MMM yyyy, HH:mm")}`}
          </p>
        )}
      </section>

      <section data-testid="summary-buffer" className="space-y-2">
        <SectionTitle>Buffer Summary</SectionTitle>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Available" value={buffer.available} testId="buf-available" valueClass="text-emerald-600" />
          <Stat label="Surplus Created" value={buffer.surplus_created} testId="buf-surplus" />
          <Stat label="Allocated to Orders" value={buffer.allocated_to_orders} testId="buf-allocated" />
          <Stat label="Owner Adjustments" value={buffer.owner_adjustments} testId="buf-adjustments" />
        </div>
        {showCosts && buffer.history?.length > 0 && (
          <div className="space-y-1.5" data-testid="summary-buffer-history">
            {buffer.history.slice(0, 20).map((e) => (
              <div key={e.id} data-testid={`summary-buffer-entry-${e.id}`} className="rounded-md border bg-card px-3 py-2">
                <p className="text-xs font-semibold">
                  {BUFFER_TYPE_LABELS[e.transaction_type] || e.transaction_type} ·{" "}
                  {["ALLOCATED_TO_NEW_ORDER", "AUTO_ORDER_ALLOCATION"].includes(e.transaction_type) ? `-${e.quantity}` : e.quantity >= 0 ? `+${e.quantity}` : e.quantity}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {e.created_by_name} · {format(new Date(e.created_at), "d MMM yyyy, HH:mm")}
                  {e.reason && ` · ${e.reason}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {ready_stock && (
        <section data-testid="summary-ready-stock" className="space-y-2">
          <SectionTitle>Ready Stock (Jubelio Handoff)</SectionTitle>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Stat label="Purchased" value={ready_stock.total_purchased} testId="rs-purchased" />
            <Stat label="Approved Received" value={ready_stock.total_approved_received} testId="rs-received" />
            <Stat label="Allocated to Customers" value={ready_stock.total_allocated_to_customers} testId="rs-allocated" />
            <Stat
              label="Remaining / Ready Stock"
              value={ready_stock.total_remaining}
              testId="rs-remaining"
              valueClass={ready_stock.total_remaining > 0 ? "text-amber-600" : "text-emerald-600"}
            />
            <Stat label="Transferred to Jubelio" value={ready_stock.total_transferred_to_jubelio} testId="rs-transferred" valueClass="text-rose-500" />
          </div>
          {ready_stock.entries?.length > 0 && (
            <div className="space-y-1.5" data-testid="summary-ready-stock-history">
              {ready_stock.entries.map((e) => (
                <div key={e.id} data-testid={`summary-ready-stock-entry-${e.id}`} className="rounded-md border bg-card px-3 py-2">
                  <p className="text-xs font-semibold">
                    {e.event === "MIGRATION_RECONCILIATION" ? "Reconciliation (migration)" : "Batch close handoff"} · {e.product_name} · +{e.quantity} unit{e.quantity === 1 ? "" : "s"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {e.created_by_name} · {format(new Date(e.created_at), "d MMM yyyy, HH:mm")}
                    {e.note && ` · ${e.note}`}
                    {showCosts && e.weighted_avg_cost_idr != null && ` · avg cost ${fmtIDR(e.weighted_avg_cost_idr)}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section data-testid="summary-products" className="space-y-2">
        <SectionTitle>Product Breakdown</SectionTitle>
        {products.length === 0 ? (
          <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">No products in this batch</p>
        ) : (
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.product_id} data-testid={`summary-product-${p.product_id}`} className="rounded-md border bg-card p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-sm font-bold">{p.name}</p>
                  <StatusBadge status={p.status} />
                </div>
                <p className="mt-1 text-xs font-semibold text-stone-600">
                  Requested {p.requested_qty} · Purchased {p.purchased_qty} · Received {p.approved_received_qty}
                  {p.allocated_to_customers > 0 && ` · Allocated ${p.allocated_to_customers}`}
                  {p.ready_stock_qty > 0 && ` · Ready Stock ${p.ready_stock_qty}`}
                  {p.buffer_qty > 0 && ` · Buffer ${p.buffer_qty}`}
                </p>
                {showCosts && p.weighted_avg_cost != null && (
                  <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`summary-cost-${p.product_id}`}>
                    Avg cost {fmtLocal(p.weighted_avg_cost, batch.currency_symbol)}
                    {p.weighted_avg_cost_idr != null && ` (${fmtIDR(p.weighted_avg_cost_idr)})`}
                    {p.estimated_margin_pct != null && ` · Est. margin ${p.estimated_margin_pct}%`}
                  </p>
                )}
                {p.locations?.length > 0 && <p className="mt-0.5 text-xs text-muted-foreground">Bought at: {p.locations.join(", ")}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {lifecycle.length > 0 && (
        <section data-testid="summary-lifecycle" className="space-y-2">
          <SectionTitle>Lifecycle History</SectionTitle>
          <div className="space-y-1.5">
            {[...lifecycle].reverse().map((e, i) => (
              <div key={i} className="rounded-md border bg-card p-3">
                <p className="text-sm font-semibold">
                  {e.event === "closed" ? "Batch closed" : e.event === "reopened" ? "Batch reopened" : e.event}
                </p>
                <p className="text-xs text-muted-foreground">
                  {e.by_name} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                </p>
                {e.reason && <p className="text-xs font-medium text-amber-700">Reason: {e.reason}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
